import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Query Key Conventions:
 *
 * All query keys follow a hierarchical pattern: [entity, scope, ...params]
 *
 * - ['machines', 'list', page?, perPage?]  — paginated machine list
 * - ['machine', id]                        — single machine detail
 * - ['templates', 'list']                  — template list
 * - ['templates', 'detail', id]            — single template detail
 * - ['templates', 'versions', templateId]  — version history for a template
 * - ['schedules']                          — schedules list
 * - ['alerts']                             — alerts list
 * - ['results', 'list']                    — inspection results list
 * - ['machineTypes', 'list']               — machine types (settings)
 * - ['machineParts', 'list']               — machine parts (settings)
 * - ['categories', 'list']                 — checklist categories (settings)
 *
 * Invalidation pattern:
 *   On every successful mutation, invalidate the parent list key so TanStack Query
 *   refetches and the list view reflects latest server state.
 *   e.g. after saving a machine → invalidateQueries({ queryKey: ['machines', 'list'] })
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — sidebar navigation within this window serves cached data without refetch
      gcTime: 5 * 60 * 1000, // 5 minutes — keep inactive cache longer for back-navigation
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export { queryClient };
